import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { friendlyError } from '@/lib/errors';

// ============================================================================
// M5 «Календарь» — стандартный виджет рабочих столов/страниц (SSoT — сервер):
// события календаря + сроки задач (источник только для чтения) в диапазоне
// день/неделя; ‹ › — листание периодов, «Сегодня» — возврат.
// ============================================================================

interface CalItem {
  id: string;
  title: string;
  startAt: string;
  allDay: boolean;
  source: string; // event|task
  kind: string;
  refId: string;
}

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

type CalProps = { title?: string; viewDays?: number; mine?: boolean; [key: string]: unknown };

export function CalendarWidget(props: CalProps) {
  const extras = props;
  const title = extras.title || 'Календарь';
  const viewDays = Math.max(1, Number(extras.viewDays) || 7);
  const mine = !!extras.mine;

  const [items, setItems] = useState<CalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);

  const range = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + offset * viewDays);
    const end = new Date(start);
    end.setDate(end.getDate() + viewDays);
    return { from: start, to: end };
  }, [offset, viewDays]);

  const load = useCallback(async () => {
    try {
      const req: Record<string, unknown> = {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      };
      if (mine) {
        const uid = useAuthStore.getState().user?.id || '';
        if (uid) req.ownerId = uid;
      }
      const r: any = await getSDK().calendar.listCalendar(req as never);
      setItems((r?.items || []).map((i: any) => ({
        id: String(i.id || ''),
        title: String(i.title || ''),
        startAt: String(i.startAt || ''),
        allDay: !!i.allDay,
        source: String(i.source || ''),
        kind: String(i.kind || ''),
        refId: String(i.refId || ''),
      })));
      setError('');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [range, mine]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Группировка по дням (ключ — календарная дата начала).
  const byDay = useMemo(() => {
    const map = new Map<string, CalItem[]>();
    for (const it of items) {
      const key = it.startAt.slice(0, 10);
      const arr = map.get(key) || [];
      arr.push(it);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return map;
  }, [items]);

  const dayKeys = useMemo(() => {
    const keys: string[] = [];
    for (let i = 0; i < viewDays; i++) {
      const d = new Date(range.from);
      d.setDate(d.getDate() + i);
      keys.push(d.toISOString().slice(0, 10));
    }
    return keys;
  }, [range, viewDays]);

  const rangeLabel = `${range.from.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} — ${new Date(range.to.getTime() - 86400000).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 text-sm font-medium shrink-0">
        <CalendarDays className="h-4 w-4" />
        <span className="truncate">{title}</span>
        <span className="ml-auto text-[11px] font-normal text-muted-foreground">{rangeLabel}</span>
        <button type="button" className="rounded border px-1 py-0.5 hover:bg-accent" title="Предыдущий период" onClick={() => setOffset((o) => o - 1)}>
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-accent" onClick={() => setOffset(0)}>Сегодня</button>
        <button type="button" className="rounded border px-1 py-0.5 hover:bg-accent" title="Следующий период" onClick={() => setOffset((o) => o + 1)}>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="rounded border px-1 py-0.5 hover:bg-accent" title="Обновить" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {error && <div className="px-1 py-1 text-[11px] text-destructive">{error}</div>}
        {loading && !items.length && <div className="px-1 py-2 text-[11px] text-muted-foreground">Загрузка…</div>}
        {dayKeys.map((key) => {
          const day = new Date(key + 'T00:00:00');
          const list = byDay.get(key) || [];
          return (
            <div key={key} className="mb-2">
              <div className={`sticky top-0 z-10 bg-card/95 px-1 py-0.5 text-[11px] font-medium ${key === new Date().toISOString().slice(0, 10) ? 'text-primary' : 'text-muted-foreground'}`}>
                {WEEKDAYS[day.getDay()]}, {day.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long' })}
                <span className="ml-1 text-muted-foreground">({list.length})</span>
              </div>
              {list.length === 0 && <div className="px-1 text-[11px] text-muted-foreground">—</div>}
              {list.map((it) => (
                <div key={it.source + it.id} className="flex items-center gap-2 rounded px-1 py-0.5 text-[11px] hover:bg-accent/5" title={it.title}>
                  <span className="w-10 shrink-0 text-muted-foreground">
                    {it.allDay ? 'весь д.' : new Date(it.startAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className={`truncate ${it.source === 'task' ? 'italic' : ''}`}>{it.title}</span>
                  {it.source === 'task' && <span className="ml-auto shrink-0 rounded bg-muted px-1 text-[9px] text-muted-foreground">задача</span>}
                  {it.source === 'event' && it.kind && <span className="ml-auto shrink-0 rounded bg-muted px-1 text-[9px] text-muted-foreground">{it.kind}</span>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
